<template>
  <d-modal
    :active="isActive"
    :width="600"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-2xl font-bold">
          {{ subjectArea.name }}
        </h3>
        <p class="text-4xl font-bold">
          Add Content
        </p>
      </div>

      <!-- Form -->
      <form @submit.prevent="saveForm" class="space-y-4">
        <!-- Title Field -->
        <d-field label="Title">
          <d-input v-model="form.name" required />
        </d-field>

        <!-- Description Field -->
        <d-field label="Description">
          <d-input v-model="form.description" type="textarea" />
        </d-field>

        <!-- Type Field -->
        <d-field label="Type">
          <d-select v-model="form.type" required expanded>
            <option value="Static">
              Static
            </option>
            <option value="Interactive">
              Interactive
            </option>
          </d-select>
        </d-field>

        <!-- Cost Field -->
        <d-field label="Cost">
          <d-input v-model.number="computedCost" type="number" step="any" />
        </d-field>

        <!-- Completion Time Field -->
        <d-field label="Completion Time (in hours)">
          <d-input v-model.number="completionLength" type="number" step="any" />
        </d-field>

        <!-- Other Configurations -->
        <d-field label="Other Configurations">
          <div class="flex flex-wrap">
            <d-switch v-model="form.active" class="w-full ml-0 mb-2">
              Active
            </d-switch>
            <d-switch v-model="form.public" class="w-full ml-0 mb-2">
              Public
            </d-switch>
            <d-switch v-if="form.type === ContentListingType.INTERACTIVE" v-model="form.grantsCertificate" class="w-full ml-0 mb-2">
              Grants Certificate
            </d-switch>
          </div>
        </d-field>

        <!-- Certificate Text Field -->
        <d-field v-if="form.grantsCertificate && form.type === ContentListingType.INTERACTIVE" label="Certificate Text">
          <d-input v-model="form.certificateText" />
        </d-field>

        <p class="font-bold text-gray-700 mt-3">
          Note: More options will be available for editing.
        </p>

        <!-- Loading State -->
        <div v-if="saving" class="flex justify-center py-4">
          <span class="loading loading-spinner loading-md"></span>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end gap-3 pt-4">
          <d-btn 
            type="button"
            flat 
            @click="handleClose"
          >
            Cancel
          </d-btn>
          <d-btn 
            type="submit"
            color="primary"
            :disabled="!isFormValid || saving"
            :loading="saving"
          >
            Add Content
          </d-btn>
        </div>
      </form>
    </div>
  </d-modal>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@stores/auth';
import { showErrorToast } from '@services/errorHandling.service';
import { ContentListingType } from '@resources/scripts/constants';

export default {
  name: 'SubjectAreaContentModal',
  
  props: {
    active: {
      type: Boolean,
      default: false
    },
    subjectArea: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      ContentListingType,
      saving: false,
      form: {
        name: '',
        description: '',
        type: 'Interactive',
        public: false,
        cost: 0,
        active: false,
        completionLength: '',
        grantsCertificate: false,
        certificateText: '',
        userId: null, // Author
        subjectAreaId: this.subjectArea.id,
        categoryIds: [],
        resourceTypeIds: [],
      },
      auth: null,
    };
  },

  computed: {
    isActive() {
      return this.active;
    },

    isFormValid() {
      return this.form.name && this.form.name.trim() !== '';
    },

    // Use Pinia auth store's profile
    profile() {
      return this.auth ? this.auth.profile || {} : {};
    },

    computedCost: {
      get() {
        return this.form.cost;
      },
      set(val) {
        this.form.cost = val ? Number.parseFloat(val) : 0;
      },
    },

    completionLength: {
      get() {
        return this.form.completionLength;
      },
      set(val) {
        this.form.completionLength = val.toString();
      },
    },
  },

  watch: {
    active(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },

    'form.type': function (val) {
      if (val === ContentListingType.STATIC) {
        this.form.certificateText = '';
        this.form.grantsCertificate = false;
      }
    },
  },

  mounted() {
    this.auth = useAuthStore();
    this.form.userId = this.profile.id;
    this.form.subjectAreaId = this.subjectArea.id;
  },

  methods: {
    resetForm() {
      this.form = {
        name: '',
        description: '',
        type: 'Interactive',
        public: false,
        cost: 0,
        active: false,
        completionLength: '',
        grantsCertificate: false,
        certificateText: '',
        userId: this.profile.id,
        subjectAreaId: this.subjectArea.id,
        categoryIds: [],
        resourceTypeIds: [],
      };
    },

    async saveForm() {
      if (!this.isFormValid) return;

      this.saving = true;
      try {
        const { data } = await axios.post('/api/content-listings', this.form);
        window.location.href = `/subject-areas/listing/${this.subjectArea.id}/edit/${data.id}`;
      } catch (error) {
        showErrorToast(error);
        this.saving = false;
      }
    },

    handleClose() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Remove any potential duplicate styling that might cause ghost outlines */
</style>